import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { CadastrosOcorrenciaProps, Ocorrencia} from './types';
import * as S from './styles';
import Table from '../../../components/interface/Tabela';
import { faTrash, faTimes, faExclamationCircle, faPlus, faSave, faEraser, faCheck } from '@fortawesome/free-solid-svg-icons';
import { Column } from '../../../components/interface/Tabela/types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Modal } from '../../../components/interface/Modal';
import InputSearchCodigo from '../../../components/interface/InputSearchCodigo';
import FormInput from '../../../components/interface/FormInput';
import ActionButtons from '../../../components/interface/ActionButtons';
import { ActionButtonProps } from '../../../components/interface/ActionButtons/types';
import { DefaultTheme } from 'styled-components';
import Store, { useAppDispatch } from '../../../store';

const CadastrosOcorrenciaView: React.FC<CadastrosOcorrenciaProps> = ({
  formData,
  error,
  success,
  searchTerm,
  ocorrencias,
  handleChange,
  handleCheckboxChange,
  handleSubmit,
  handleSearch,
  handleSearchInputChange,
  handleEdit,
  handleDelete,
  setOcorrencias,
  setSearchTerm,
  setSuccess,
  handleAddSubocorrencia,
  handleRemoveSubocorrencia,
  handleSubocorrenciaChange,
  setFormData,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [ocorrenciaToDelete, setOcorrenciaToDelete] = useState<number | null>(null);
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
  const dispatch = useAppDispatch();
  const handleSelectOcorrencia = useCallback((ocorrencia: Ocorrencia) => {
    handleEdit(ocorrencia);
    setIsModalOpen(false);
  }, [handleEdit]);

  // Atualizar o useEffect para selecionar automaticamente quando houver apenas uma ocorrência na lista
  useEffect(() => {
    if (ocorrencias.length === 1) {
      handleSelectOcorrencia(ocorrencias[0]);
    } else if (ocorrencias.length > 1) {
      setIsModalOpen(true);
    }
  }, [ocorrencias, handleSelectOcorrencia]);

  const handleDeleteConfirmation = useCallback((codigo: number) => {
    if (!codigo) {
      Store.dialog.alert(dispatch, 'Informe o código da ocorrência', 'error');
      return;
    }
    setOcorrenciaToDelete(codigo);
    setConfirmDeleteModalOpen(true);
  }, [dispatch]);

  const handleConfirmDelete = useCallback(() => {
    if (ocorrenciaToDelete !== null) {
      handleDelete(ocorrenciaToDelete);
      setConfirmDeleteModalOpen(false);
      setOcorrenciaToDelete(null);
    }
  }, [ocorrenciaToDelete, handleDelete]);

  const handleCancelDelete = useCallback(() => {
    setConfirmDeleteModalOpen(false);
    setOcorrenciaToDelete(null);
  }, []);

  const handleCloseModal = useCallback(() => {
    setOcorrencias([]);
    setSuccess('');
    setIsModalOpen(false);
  }, [setOcorrencias, setSuccess]);

  const handleClearSearch = useCallback(() => {
    setOcorrencias([]);
    setSearchTerm('');
    setSuccess('');
    setIsModalOpen(false);

    const empresa = formData.empresa;
    setFormData({
      codigo: undefined,
      descricao: '',
      subocorrencia: false,
      fotocanhoto: false,
      justificativa: false,
      empresa,
      subocorrencias: [],
    });
  }, [setOcorrencias, setSearchTerm, setSuccess, setFormData, formData.empresa]);

  const handleSearchOnBlur = useCallback(() => {
    handleSearch();
  }, [handleSearch]);

  const columns = useMemo<Column<Ocorrencia>[]>(() => [
    {
      key: 'codigo',
      header: 'Código',
      filterable: true,
      render: (ocorrencia) => ocorrencia.codigo.toString(),
    },
    {
      key: 'descricao',
      header: 'Descrição',
      filterable: true,
    },
    {
      key: 'subocorrencia',
      header: 'Subocorrência',
      filterable: true,
      render: (ocorrencia) => (ocorrencia.subocorrencia ? 'Sim' : 'Não'),
    },
    {
      key: 'fotocanhoto',
      header: 'Foto Canhoto',
      filterable: true,
      render: (ocorrencia) => (ocorrencia.fotocanhoto ? 'Sim' : 'Não'),
    },
    {
      key: 'justificativa',
      header: 'Justificativa',
      filterable: true,
      render: (ocorrencia) => (ocorrencia.justificativa ? 'Sim' : 'Não'),
    },
    {
      key: 'actions',
      header: '',
      width: '100px',
    },
  ], []);

  const actionButtons = useMemo(() => [
    {
      icon: faSave,
      onClick: handleSubmit,
      title: 'Salvar',
      backgroundColor: (theme: DefaultTheme) => theme.primaryColor,
    },
    {
      icon: faTrash,
      onClick: () => handleDeleteConfirmation(formData.codigo || 0),
      title: 'Excluir',
      backgroundColor: '#dc3545',
    },
    {
      icon: faEraser,
      onClick: handleClearSearch,
      title: 'Limpar',
      backgroundColor: '#4dc9c9',
    },
  ], [handleSubmit, handleDeleteConfirmation, formData.codigo, handleClearSearch]);

  const modalActions = useMemo(() => [
    {
      icon: faCheck,
      onClick: handleSelectOcorrencia,
      title: 'Selecionar',
    },
  ], [handleSelectOcorrencia]);

  return (
    <S.CadastrosContainer>
      <S.CadastrosContent>
        <S.FormContainer>
          <div style={{ display: 'flex', justifyContent: 'start',marginLeft: '3.7em'}}>
          <InputSearchCodigo
            label="Código:"
            placeholder="Buscar ocorrência por código"
            value={searchTerm}
            onChange={handleSearchInputChange}
            onSearch={handleSearch}
            onBlur={handleSearchOnBlur}
            width="400px"
          />
          </div>
          {error && <S.ErrorMessage>{error}</S.ErrorMessage>}
          {success && <S.SuccessMessage>{success}</S.SuccessMessage>}

          {/* Modal de seleção de ocorrências */}
          <Modal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            title={`Selecionar Ocorrência (${ocorrencias.length})`}
            headerIcon={faExclamationCircle}
            size="large"
          >
            <Table
              data={ocorrencias}
              columns={columns}
              actions={modalActions}
              exportable={true}
            />
          </Modal>

          {/* Modal de confirmação de exclusão */}
          <Modal
            isOpen={confirmDeleteModalOpen}
            onClose={handleCancelDelete}
            title="Confirmar Exclusão"
            headerIcon={faTrash}
            size="small"
          >
            <S.ModalContent>
              <p>Tem certeza que deseja excluir esta ocorrência?</p>
              <p>Esta ação não pode ser desfeita.</p>
              <S.ModalButtonsContainer>
                <S.Button
                  type="button"
                  onClick={handleCancelDelete}
                  style={{ backgroundColor: '#6c757d' }}
                >
                  <FontAwesomeIcon icon={faTimes} /> Cancelar
                </S.Button>
                <S.Button
                  type="button"
                  onClick={handleConfirmDelete}
                  style={{ backgroundColor: '#dc3545' }}
                >
                  <FontAwesomeIcon icon={faTrash} /> Excluir
                </S.Button>
              </S.ModalButtonsContainer>
            </S.ModalContent>
          </Modal>

          <S.Form onSubmit={handleSubmit}>
            <S.FormRow>
              <FormInput
                label="Descrição:"
                name="descricao"
                value={formData.descricao}
                onChange={handleChange}
                width="460px"
                required
              />
            </S.FormRow>

            <S.FormRow>
              <S.FormGroup>
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginLeft: '3.7em' }}>
                  <S.Checkbox>
                    <input
                      type="checkbox"
                      id="subocorrencia"
                      name="subocorrencia"
                      checked={formData.subocorrencia}
                      onChange={handleCheckboxChange}
                    />
                    <S.Label htmlFor="subocorrencia" style={{ marginBottom: 0 }}>Possui Subocorrência</S.Label>
                  </S.Checkbox>

                  <S.Checkbox>
                    <input
                      type="checkbox"
                      id="fotocanhoto"
                      name="fotocanhoto"
                      checked={formData.fotocanhoto}
                      onChange={handleCheckboxChange}
                    />
                    <S.Label htmlFor="fotocanhoto" style={{ marginBottom: 0 }}>Foto Canhoto</S.Label>
                  </S.Checkbox>

                  <S.Checkbox>
                    <input
                      type="checkbox"
                      id="justificativa"
                      name="justificativa"
                      checked={formData.justificativa}
                      onChange={handleCheckboxChange}
                    />
                    <S.Label htmlFor="justificativa" style={{ marginBottom: 0 }}>Justificativa</S.Label>
                  </S.Checkbox>
                </div>
              </S.FormGroup>
            </S.FormRow>

            {formData.subocorrencia && (
              <S.SubocorrenciasContainer>
                <S.SubocorrenciaTitle>Subocorrências</S.SubocorrenciaTitle>

                {formData.subocorrencias.map((subocorrencia, index) => (
                  <S.SubocorrenciaItem key={index}>
                    <S.SubocorrenciaInput
                      type="text"
                      placeholder="Descrição da subocorrência"
                      value={subocorrencia.descricao}
                      onChange={(e) =>
                        handleSubocorrenciaChange(
                          index,
                          'descricao',
                          e.target.value
                        )
                      }
                    />
                    <S.Checkbox>
                      <input
                        type="checkbox"
                        checked={subocorrencia.justificativa}
                        onChange={(e) =>
                          handleSubocorrenciaChange(
                            index,
                            'justificativa',
                            e.target.checked
                          )
                        }
                      />
                      <span>Justificativa</span>
                    </S.Checkbox>
                    <S.RemoveButton
                      onClick={() => handleRemoveSubocorrencia(index)}
                    >
                      <FontAwesomeIcon icon={faTrash}/>
                    </S.RemoveButton>
                  </S.SubocorrenciaItem>
                ))}

                <S.AddButton type="button" onClick={handleAddSubocorrencia}>
                  <FontAwesomeIcon icon={faPlus} />
                </S.AddButton>
              </S.SubocorrenciasContainer>
            )}

            {/* Botões de ação no final do formulário */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
              <ActionButtons
                buttons={actionButtons as ActionButtonProps[]}
              />
            </div>
          </S.Form>
        </S.FormContainer>
      </S.CadastrosContent>
    </S.CadastrosContainer>
  );
};

export default CadastrosOcorrenciaView;
